// src/app/shared/components/tour/tour-overlay.component.ts
import {
  Component, OnInit, OnDestroy, inject, signal, computed, effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { TourService } from '@core/services/tour.service';
import { LanguageService } from '@core/services/language.service';
import { AuthService } from '@features/auth/services/auth.service';
import { Router } from '@angular/router';
import { environment } from '@env/environment';

@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    TranslateModule
  ],
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.scss'
})
export class TourOverlayComponent implements OnInit, OnDestroy {
  readonly tourService = inject(TourService);
  readonly languageService = inject(LanguageService);
  private readonly translate = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly languages = [
    { code: 'en', flag: '🇬🇧', name: 'English' },
    { code: 'he', flag: '🇮🇱', name: 'עברית' },
    { code: 'ar', flag: '🇸🇦', name: 'العربية' },
    { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
    { code: 'es', flag: '🇪🇸', name: 'Español' },
    { code: 'fr', flag: '🇫🇷', name: 'Français' },
    { code: 'it', flag: '🇮🇹', name: 'Italiano' },
    { code: 'pt', flag: '🇧🇷', name: 'Português' },
    { code: 'ru', flag: '🇷🇺', name: 'Русский' },
    { code: 'uk', flag: '🇺🇦', name: 'Українська' },
    { code: 'pl', flag: '🇵🇱', name: 'Polski' },
    { code: 'nl', flag: '🇳🇱', name: 'Nederlands' },
    { code: 'ro', flag: '🇷🇴', name: 'Română' },
    { code: 'el', flag: '🇬🇷', name: 'Ελληνικά' },
    { code: 'zh', flag: '🇨🇳', name: '中文' },
    { code: 'ja', flag: '🇯🇵', name: '日本語' },
    { code: 'ko', flag: '🇰🇷', name: '한국어' },
    { code: 'hi', flag: '🇮🇳', name: 'हिंदी' },
    { code: 'th', flag: '🇹🇭', name: 'ไทย' },
    { code: 'tr', flag: '🇹🇷', name: 'Türkçe' },
    { code: 'vi', flag: '🇻🇳', name: 'Tiếng Việt' },
    { code: 'id', flag: '🇮🇩', name: 'Indonesia' },
    { code: 'ms', flag: '🇲🇾', name: 'Melayu' },
    { code: 'am', flag: '🇪🇹', name: 'አማርኛ' },
  ];

  // Spotlight position
  spotlightTop = signal(0);
  spotlightLeft = signal(0);
  spotlightWidth = signal(0);
  spotlightHeight = signal(0);

  // Tooltip position
  tooltipTop = signal(0);
  tooltipLeft = signal(0);

  // Feedback form
  feedbackEmail = signal('');
  feedbackMessage = signal('');
  feedbackSent = signal(false);
  feedbackLoading = signal(false);

  private scrollHandler = () => this.refreshPosition();
  private resizeHandler = () => this.refreshPosition();

  // Interactive click listener management
  private interactiveEl: HTMLElement | null = null;
  private interactiveHandler: (() => void) | null = null;

  constructor() {
    // Auto-detect browser language for first-time visitors (no saved preference)
    if (!localStorage.getItem('preferredLanguage')) {
      const browserLang = navigator.language?.split('-')[0];
      const supported = this.languageService.supportedLanguages;
      if (browserLang && supported.includes(browserLang)) {
        this.languageService.setLanguage(browserLang);
      }
    }

    // Re-place spotlight whenever step changes; attach/detach interactive listener
    effect(() => {
      const step = this.tourService.currentStepData();
      this.detachInteractiveListener();
      if (this.tourService.isActive() && step) {
        setTimeout(() => {
          this.placeSpotlight(step.elementId);
          if (step.interactive) {
            this.attachInteractiveListener(step.elementId);
          }
        }, step.navigationDelay ?? 100);
      }
    });
  }

  ngOnInit(): void {
    window.addEventListener('scroll', this.scrollHandler, true);
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scrollHandler, true);
    window.removeEventListener('resize', this.resizeHandler);
    this.detachInteractiveListener();
  }

  private attachInteractiveListener(elementId: string): void {
    const el = document.getElementById(elementId);
    if (!el) return;
    const handler = () => {
      this.detachInteractiveListener();
      this.tourService.next();
    };
    el.addEventListener('click', handler, { once: true });
    this.interactiveEl = el;
    this.interactiveHandler = handler;
  }

  private detachInteractiveListener(): void {
    if (this.interactiveEl && this.interactiveHandler) {
      this.interactiveEl.removeEventListener('click', this.interactiveHandler);
    }
    this.interactiveEl = null;
    this.interactiveHandler = null;
  }

  placeSpotlight(elementId: string): void {
    const el = document.getElementById(elementId);
    if (!el) return;

    const step = this.tourService.currentStepData();
    // v2 shell uses `.content` inside `.main`; v1 used `.shell-content`.
    const scrollContainer = (
      document.querySelector('.dash-v2 .content') ||
      document.querySelector('.shell-v2 .content') ||
      document.querySelector('.main > .content') ||
      document.querySelector('.shell-content')
    ) as HTMLElement | null;
    // Topbar: v2 has `.topbar` (always rendered); v1 had `.mobile-topbar` (mobile-only).
    const topbarEl = (
      document.querySelector('.topbar') ||
      (window.innerWidth < 768 ? document.querySelector('.mobile-topbar') : null)
    ) as HTMLElement | null;

    // Scroll element into view based on preferred tooltip direction
    const scrollBlock: ScrollLogicalPosition = step?.position === 'top' ? 'end' : 'start';
    el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: scrollBlock });

    // After initial scroll, measure any sticky page header so we can clear it
    // Includes v1 (.dash-header / .cl-header / …) and v2 (.dash-v2-card-head / .inv-form-v2-chrome / etc.)
    const stickyPageHeader = scrollContainer?.querySelector(
      '.dash-header, .cl-header, .inv-header, .pr-header, ' +
      '.dash-v2-card-head, .inv-form-v2-chrome, .reg-v2-brand, .reports-v2-head'
    ) as HTMLElement | null;
    const stickyHeaderBottom = stickyPageHeader ? stickyPageHeader.getBoundingClientRect().bottom : 0;
    const topOffset = Math.max(
      (topbarEl ? topbarEl.getBoundingClientRect().bottom : 0) + 8,
      stickyHeaderBottom + 4
    );

    // Nudge scroll container if element is hidden behind sticky/fixed header
    if (scrollContainer) {
      const elTop = el.getBoundingClientRect().top;
      if (elTop < topOffset) scrollContainer.scrollBy(0, elTop - topOffset);
    }

    const rect = el.getBoundingClientRect();
    const padding = 6;
    const vh = window.innerHeight;

    // Clip spotlight to viewport; never start above the sticky topbar/header
    const spotTop = Math.max(rect.top - padding, topOffset);
    const spotBottom = Math.min(rect.bottom + padding, vh);
    this.spotlightTop.set(spotTop);
    this.spotlightLeft.set(rect.left - padding);
    this.spotlightWidth.set(rect.width + padding * 2);
    this.spotlightHeight.set(Math.max(spotBottom - spotTop, 0));

    this.positionTooltip(rect, topOffset);
    requestAnimationFrame(() => this.positionTooltip(el.getBoundingClientRect(), topOffset));
  }

  private positionTooltip(rect: DOMRect, topOffset = 0): void {
    const stickyHeaderBottom = topOffset;
    const step = this.tourService.currentStepData();
    const tooltipEl = document.querySelector('.tour-tooltip') as HTMLElement;
    const tooltipH = tooltipEl ? tooltipEl.offsetHeight + 10 : 350;
    const tooltipW = 300;
    const margin = 16;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    if (step?.id === 'pip-timer') {
      this.tooltipTop.set(margin);
      this.tooltipLeft.set(Math.round(vw / 2) - Math.round(tooltipW / 2));
      return;
    }

    const isRtl = document.dir === 'rtl';
    let left = isRtl ? rect.right - tooltipW : rect.left;
    left = Math.max(margin, Math.min(left, vw - tooltipW - margin));

    // Use the visible (viewport-clamped) bounds for placement logic
    const topBound = Math.max(margin, stickyHeaderBottom);
    const visibleTop = Math.max(rect.top, topBound);
    const visibleBottom = Math.min(rect.bottom, vh - margin);
    const spaceAbove = visibleTop - topBound;
    const spaceBelow = vh - visibleBottom - margin;

    let top: number;
    if (step?.position === 'bottom' && spaceBelow >= tooltipH) {
      top = visibleBottom + margin;                     // below element
    } else if (spaceAbove >= tooltipH) {
      top = visibleTop - tooltipH - margin;             // above element
    } else if (spaceBelow >= tooltipH && step?.position !== 'top') {
      top = visibleBottom + margin;                     // fallback: below (only when not preferring top)
    } else {
      // No good external position: pin tooltip to top of spotlight (clamped by topBound).
      // Prefers covering the spotlighted element over covering unrelated content below it.
      top = visibleTop - tooltipH - margin;
    }

    top = Math.max(topBound, Math.min(top, vh - tooltipH - margin));
    this.tooltipTop.set(top);
    this.tooltipLeft.set(left);
  }

  refreshPosition(): void {
    const step = this.tourService.currentStepData();
    if (step && this.tourService.isActive()) {
      this.placeSpotlight(step.elementId);
    }
  }

  isMobile(): boolean {
    return window.innerWidth < 768;
  }

  goRegister(): void {
    this.authService.logout().subscribe({
      complete: () => this.router.navigate(['/auth/register']),
      error: () => this.router.navigate(['/auth/register'])
    });
  }

  submitFeedback(): void {
    if (this.feedbackLoading()) return;
    this.feedbackLoading.set(true);
    this.http.post(`${environment.apiUrl}/feedback`, {
      email: this.feedbackEmail(),
      message: this.feedbackMessage(),
      source: 'demo-tour'
    }).pipe(
      catchError(() => of(null))
    ).subscribe(() => {
      this.feedbackLoading.set(false);
      this.feedbackSent.set(true);
    });
  }
}
